import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { skipToken } from '@reduxjs/toolkit/query'

import { styled } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Grid'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Stepper from '@mui/material/Stepper'
import Step from '@mui/material/Step'
import StepConnector, { stepConnectorClasses } from '@mui/material/StepConnector'
import { StepIconProps } from '@mui/material/StepIcon'
import StepLabel from '@mui/material/StepLabel'
import Typography from '@mui/material/Typography'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'

import { OrderStatus } from '@/core/models/order.model'
import { useGetOrderByReferenceIdQuery } from '@/core/services/order'

import { isMeetingLinkOrder } from '@/utils/type-guards'

const Connector = styled(StepConnector)(({ theme }) => ({
  marginLeft: '15px',
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: theme.palette.success.main,
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: theme.palette.success.main,
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: '#E5E7EB',
    borderLeftWidth: 2,
    minHeight: '50px',
  },
}))

function StepIcon(props: StepIconProps) {
  const { active, completed, error, sx = [] } = props
  return (
    <Stack>
      <Box
        className={active ? 'pulse-success' : ''}
        sx={[
          {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '32px',
            height: '32px',
            background: active || (completed && !error) ? '#38A169' : error ? '#E02424' : '#E5E7EB',
            borderRadius: '16px',
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        {active || completed ? <CheckRoundedIcon fontSize="small" htmlColor="#FFF" /> : null}
      </Box>
      <Box
        sx={[{ flex: 1, ml: '16px', background: '#E5E7EB', width: '1px' }, !!active && { background: '#2F855A' }, !!completed && { background: '#2F855A' }]}
      />
    </Stack>
  )
}

export function OrderTrackingPage() {
  const [searchParams] = useSearchParams()
  const { t } = useTranslation()

  const [activeStep, setActiveStep] = useState(0)

  const orderReferenceId = searchParams.get('order')

  const { data: order } = useGetOrderByReferenceIdQuery(orderReferenceId ?? skipToken)

  const steps = useMemo(
    () => [
      {
        id: 'orderCreated',
        label: t('page.orderTracking.orderStatus.created.label'),
      },
      {
        id: 'orderAccepted',
        label: t('page.orderTracking.orderStatus.accepted.label'),
      },
    ],
    [t]
  )

  useEffect(() => {
    if (!order) return

    switch (order.status) {
      case OrderStatus.Pending:
        setActiveStep(0)
        break
      case OrderStatus.Accepted:
        setActiveStep(1)
        break
      default:
        break
    }
  }, [order])

  return (
    <Stack alignItems="center" justifyContent="center" height="100%">
      {order && (
        <Paper sx={{ overflow: 'hidden', margin: '10px' }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            sx={{ backgroundColor: (theme) => theme.palette.primary.main, color: (theme) => theme.palette.common.white, p: '15px 20px' }}
          >
            <Typography>{t('page.orderTracking.orderReference', { referenceId: order.referenceId })}</Typography>
            {(order.status === OrderStatus.Canceled || order.status === OrderStatus.Refused) && (
              <Chip color="error" label={t(`common:model.order.status.${order.status}.label`)} size="small" variant="filled" />
            )}
          </Stack>
          <Grid container spacing={2}>
            <Grid item xs={5} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Stepper activeStep={activeStep} connector={<Connector />} orientation="vertical" sx={{ pl: 2 }}>
                {steps.map((step) => (
                  <Step key={step.id} sx={{ p: 0 }}>
                    <StepLabel
                      StepIconComponent={StepIcon}
                      sx={{
                        alignItems: 'center',
                        flex: 1,
                        padding: 0,
                        '& .MuiStepLabel-label': {
                          fontSize: '14px',
                          lineHeight: '16px',
                        },
                        '& .MuiStepLabel-label.Mui-completed, & .MuiStepLabel-label.Mui-active': {
                          fontWeight: 600,
                        },
                        '& .MuiStepLabel-iconContainer': {
                          pr: '16px',
                        },
                      }}
                    >
                      {step.label}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Grid>
            <Grid item xs={7}>
              <List>
                {isMeetingLinkOrder(order) && (
                  <ListItem>
                    <ListItemText primary={t('field.date.label')} secondary={dayjs(order.startDate).format('DD/MM/YYYY HH[h]mm')} />
                  </ListItem>
                )}
                {isMeetingLinkOrder(order) && (
                  <ListItem>
                    <ListItemText primary={t('field.speciality.label')} secondary={order.speciality} />
                  </ListItem>
                )}
                {isMeetingLinkOrder(order) && (
                  <ListItem>
                    <ListItemText primary={t('field.address.label')} secondary={order.meetingPlace.description} />
                  </ListItem>
                )}
                {isMeetingLinkOrder(order) && (
                  <ListItem>
                    <ListItemText primary={t('field.patient.label')} secondary={order.createdBy.userInfo.name + ' ' + order.createdBy.userInfo.surname} />
                  </ListItem>
                )}
              </List>
            </Grid>
          </Grid>
        </Paper>
      )}
    </Stack>
  )
}

export default OrderTrackingPage
